/**
 * Client-side image downscaling for uploads with a hard backend size cap.
 *
 * Phone photos are routinely 3-12MB (JPEG/HEIC), which the identity-proof
 * backend rejects (Max 6MB). We re-encode oversized images to a JPEG that
 * fits under the cap before they ever leave the device. PDFs and files that
 * already fit are passed through untouched, and any decode/encode failure
 * falls back to the original file so the upload flow never breaks.
 */

const DEFAULT_MAX_DIMENSION = 2000; // longest side, px -- plenty for ID legibility
const DEFAULT_MAX_BYTES = 5.5 * 1024 * 1024; // stay safely under the 6MB backend cap
// Give up, release the in-flight work and hand back the original if
// decode/encode stalls (Safari has been seen never firing the toBlob callback
// on very large canvases under memory pressure); the caller must never be left
// waiting on us.
const DEFAULT_TIMEOUT_MS = 15000;
const OUTPUT_MIME = 'image/jpeg';
const QUALITY_START = 0.85;
const QUALITY_MIN = 0.5;
const QUALITY_STEP = 0.1;

// General-purpose helper: the regex stays broad (webp/heic may arrive from
// other callers). The IDVerification file input gates formats upstream via its
// `accept` attribute, where heic typically reaches us already converted to jpeg.
const RESIZABLE_FORMATS = 'jpe?g|png|gif|webp|heic|heif';
const RESIZABLE_MIME = new RegExp(`^image\\/(${RESIZABLE_FORMATS})$`, 'i');
// Some Android / desktop pickers hand over `type: ''`; fall back to the name.
const RESIZABLE_EXT = new RegExp(`\\.(${RESIZABLE_FORMATS})$`, 'i');

export const isResizableImage = (file) => {
  if (!file) {
    return false;
  }

  if (typeof file.type === 'string' && file.type) {
    return RESIZABLE_MIME.test(file.type);
  }

  return typeof file.name === 'string' && RESIZABLE_EXT.test(file.name);
};

/**
 * Scale dimensions so the longest side is at most maxDimension, preserving
 * aspect ratio. Returns the input unchanged when already within bounds.
 */
export const computeTargetDimensions = (width, height, maxDimension = DEFAULT_MAX_DIMENSION) => {
  if (!width || !height) {
    return { width, height };
  }

  const longest = Math.max(width, height);
  if (longest <= maxDimension) {
    return { width, height };
  }

  const scale = maxDimension / longest;
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
};

const renameToJpg = (name) =>
  typeof name === 'string' && name ? `${name.replace(/\.[^./\\]+$/, '')}.jpg` : 'upload.jpg';

// Live resources of one resize attempt, so a timeout can release them instead
// of leaving a multi-megapixel decode and canvas alive on a phone that is
// already short on memory (iOS keeps a canvas backing store around until its
// dimensions are reset).
const createRun = () => {
  const held = {};
  let cancelled = false;

  return {
    isCancelled: () => cancelled,
    hold: (resources) => Object.assign(held, resources),
    cancel: () => {
      cancelled = true;
      if (held.reader) {
        // No-op once the read is DONE, and browsers differ on whether that
        // still nulls `result`; dropping our reference below is what actually
        // lets the data URL go.
        held.reader.abort();
      }
      if (held.img) {
        held.img.onload = null;
        held.img.onerror = null;
        held.img.src = '';
      }
      if (held.canvas) {
        held.canvas.width = 0;
        held.canvas.height = 0;
      }
      // The suspended resize still references this run; make sure it keeps
      // nothing heavy alive through it.
      held.reader = null;
      held.img = null;
      held.canvas = null;
    },
  };
};

// Matches the repo's existing FileReader -> Image idiom (cottages image upload).
// Data URLs avoid the createObjectURL/revoke lifecycle and are reliable on iOS.
const loadImage = (file, run) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    run.hold({ reader });
    reader.onload = () => {
      // A read that lands after cancellation must not start a decode.
      if (run.isCancelled()) {
        reject(new Error('file read cancelled'));
        return;
      }
      const img = new Image();
      run.hold({ img });
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('image decode failed'));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error('file read failed'));
    reader.onabort = () => reject(new Error('file read cancelled'));
    reader.readAsDataURL(file);
  });

const canvasToBlob = (canvas, quality) =>
  new Promise((resolve) => {
    if (typeof canvas.toBlob === 'function') {
      canvas.toBlob((blob) => resolve(blob), OUTPUT_MIME, quality);
      return;
    }
    resolve(null);
  });

// Resolves with null if `promise` hasn't settled within `ms`, calling
// `onTimeout` so the caller can release whatever the promise was working on.
const withTimeout = (promise, ms, onTimeout) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      // Resolve first so a throwing cleanup can never leave the caller hanging;
      // the awaiting caller still resumes only after this callback returns.
      resolve(null);
      onTimeout();
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });

const resizeViaCanvas = async (file, maxDimension, maxBytes, run) => {
  const img = await loadImage(file, run);
  if (run.isCancelled()) {
    return null;
  }

  const { width, height } = computeTargetDimensions(
    img.naturalWidth || img.width,
    img.naturalHeight || img.height,
    maxDimension,
  );

  const canvas = document.createElement('canvas');
  run.hold({ canvas });
  canvas.width = width;
  canvas.height = height;
  // Drop the backing store as soon as we're done with it rather than whenever
  // GC gets to it.
  const releaseCanvas = () => {
    canvas.width = 0;
    canvas.height = 0;
  };

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    releaseCanvas();
    return null;
  }

  // White backdrop so transparent PNGs don't flatten to black in JPEG.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  // EXIF orientation is not carried over. Browsers that apply it on decode
  // (iOS/Safari >= 13.4, Chrome >= 81, Firefox >= 77) draw the photo upright,
  // so the re-encoded JPEG needs no tag. Older browsers would draw a portrait
  // photo sideways; accepted as a rare legacy case rather than parsing EXIF.
  ctx.drawImage(img, 0, 0, width, height);

  let quality = QUALITY_START;
  let best = await canvasToBlob(canvas, quality);
  let blob = best;
  while (!run.isCancelled() && blob && blob.size > maxBytes && quality > QUALITY_MIN) {
    quality = Math.max(QUALITY_MIN, quality - QUALITY_STEP);
    // eslint-disable-next-line no-await-in-loop
    blob = await canvasToBlob(canvas, quality);
    // A later encode can fail (null) where an earlier one succeeded; keep the
    // smallest usable result and stop stepping instead of falling all the way
    // back to the original.
    if (blob && blob.size < best.size) {
      best = blob;
    }
  }

  releaseCanvas();

  if (run.isCancelled() || !best) {
    return null;
  }

  return new File([best], renameToJpg(file.name), {
    type: OUTPUT_MIME,
    lastModified: file.lastModified,
  });
};

/**
 * Returns a (possibly re-encoded) File that fits under maxBytes. Non-image
 * files and images already under the cap are returned unchanged. On any
 * failure or timeout the original file is returned so the caller can still
 * attempt the upload (the backend remains the source of truth for rejection).
 * Never rejects.
 */
export const downscaleImageFile = async (file, opts = {}) => {
  const maxBytes = opts.maxBytes ?? DEFAULT_MAX_BYTES;
  const maxDimension = opts.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  if (!isResizableImage(file) || file.size <= maxBytes) {
    return file;
  }

  const run = createRun();
  try {
    const resized = await withTimeout(
      resizeViaCanvas(file, maxDimension, maxBytes, run),
      timeoutMs,
      run.cancel,
    );
    // If quality-stepping couldn't get fully under the cap we still return the
    // smaller result (best effort) and let the backend remain the final gate;
    // we only keep the original when re-encoding wouldn't shrink it at all.
    return resized && resized.size < file.size ? resized : file;
  } catch (e) {
    return file;
  }
};
